import { IconShield } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DonationReceivedCard } from '../components/DonationReceivedCard';
import { DonationsInboxEmpty } from '../components/DonationsInboxEmpty';
import { EmptyState } from '../components/EmptyState';
import { LoadMore } from '../components/LoadMore';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Toast } from '../components/Toast';
import { buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { canManageDonations, createDonationInbox } from '../lib/donationInbox';
import {
  DONATIONS_POLL_MS,
  type DonationRow,
  fetchDonationsReceivedPage,
  type ModelOption,
  useDonationsReceived,
} from '../lib/donations';
import { orgHref } from '../lib/org';
import { usePagedQuery } from '../lib/paged';
import { SESSION_TOKEN, useMe } from '../lib/session';

// Donations the caller's repos RECEIVED (arc 101 slice 11, plan doc 05) — the
// maintainer half of `/v1/donations`. Authorized server-side against the OWNER of
// the pinned repo, a different principal from the donor: a maintainer reads gifts
// they were given, picks the model they run on, and may DECLINE one — but can
// never revoke it on the donor's behalf (that is the donor's own `DELETE`).
//
// Container. Four rules it holds that nothing below it does:
//
//   - **Decline is the maintainer's own lever.** Someone else's box running
//     against your repository is a trust decision, so refusing a gift cannot
//     belong only to the giver. It confirms first and re-reads after.
//   - **Nothing is patched from an echo.** Both writes call `reload()`; the row
//     is the authority for what a gift is doing, not a 200 body.
//   - **Parked and pending gifts stay listed** — capacity vanishing silently is
//     the failure this page exists to prevent.
//   - **The disclosure is stated, not implied.** Donated capacity runs the same
//     disclosed bot as the account's own; a donor buys throughput, never a second
//     identity on the project.

export function DonationsReceived(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  // The received page carries `model_options` BESIDE its rows and the paging
  // primitive projects rows only, so the query is captured here — the picker's
  // vocabulary rides the fetch the list already made rather than a second one.
  let page: ReturnType<typeof useDonationsReceived> | undefined;
  const gifts = usePagedQuery<DonationRow>(
    (cursor) => {
      page = useDonationsReceived(cursor, token);
      return page;
    },
    {
      poll: {
        every: () => DONATIONS_POLL_MS,
        read: (cursor) => fetchDonationsReceivedPage(cursor, token),
      },
    },
  );
  const catalog = (): readonly ModelOption[] => page?.data?.model_options ?? [];

  const me = useMe(token);
  const inbox = createDonationInbox({ token, reload: gifts.reload });
  // The `canManageDonations` mirror (`write:donations` = the ADMIN ceiling, not
  // the owner-only one), SHUT while `/v1/me` is in flight: mid-load `role` is
  // `undefined`, the same shape as the PAT session that deliberately keeps the
  // control. The server stays the authority; this only hides a plain 403.
  const offered = (): boolean => !me.isLoading && canManageDonations(me.data?.role);
  const hasSession = token !== null;

  return (
    <section class="page donations-received">
      <PageHeader
        title={t('donations.received_title')}
        desc={t('donations.received_subtitle')}
        actions={
          <A class={buttonClass({ variant: 'outline', size: 'sm' })} href={orgHref('/donations')}>
            {t('donations.given_link')}
          </A>
        }
      />

      <Show
        when={hasSession}
        fallback={<EmptyState message={t('donations.received_no_session')} />}
      >
        <div class="alert alert--icon" role="note">
          <IconShield />
          <p class="alert__desc">{t('donations.received_intro')}</p>
        </div>

        <Panel title={t('donations.received_heading')}>
          <QueryBoundary
            query={gifts.view}
            isEmpty={(rows) => rows.length === 0}
            loading={<CardGridSkeleton listClass="grid-cards-3" />}
            empty={<DonationsInboxEmpty />}
          >
            {(rows) => (
              <>
                <ul class="grid-cards-3">
                  <For each={rows}>
                    {(row) => (
                      <DonationReceivedCard
                        row={row}
                        catalog={catalog()}
                        offered={offered()}
                        busy={inbox.isBusy(row.id)}
                        onChoose={inbox.choose}
                        onDecline={inbox.askDecline}
                      />
                    )}
                  </For>
                </ul>
                <LoadMore list={gifts} />
              </>
            )}
          </QueryBoundary>
        </Panel>

        <ConfirmDialog
          open={inbox.declining() !== null}
          title={t('donations.decline_title')}
          // A `money` gift runs nothing on the project — declining one only
          // unlists a backer, and their subscription stays theirs to cancel. The
          // resource line would be a lie there, so the confirm says which it is.
          desc={t(
            inbox.declining()?.kind === 'money'
              ? 'donations.decline_desc_money'
              : 'donations.decline_desc',
          )}
          destructive
          cancelLabel={t('donations.decline_cancel')}
          actionLabel={t('donations.decline_confirm')}
          onCancel={inbox.cancelDecline}
          onConfirm={inbox.confirmDecline}
        />
        <Toast message={inbox.error()} tone="error" onDismiss={inbox.clearError} />
      </Show>
    </section>
  );
}
